import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Github, Code, Smartphone, Database, Globe } from 'lucide-react';

const Projects = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: 'Automatisation de recherche d\'écoles (n8n)',
      icon: Globe,
      description: "Workflow n8n pour automatiser la recherche d'écoles selon des critères (type, ville, nombre). Utilise HTTP Request, Split Out et Google Sheets pour récupérer et stocker les résultats.",
      technologies: ['n8n', 'Google Sheets', 'API'],
      github: 'https://github.com/HAMZAZAWAK17/Automating-School-Search-with-n8n',
      period: '2025',
      coverImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center'
    },
    {
      id: 2,
      title: 'Student Management',
      icon: Database,
      description: "Système de gestion des étudiants (Java, Swing, MySQL). Permet l'inscription, la gestion des notes, la génération de rapports et l'administration des dossiers étudiants.",
      technologies: ['Java', 'Swing', 'MySQL'],
      github: 'https://github.com/HAMZAZAWAK17/Student-management',
      period: '2025',
      coverImage: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center'
    },
    {
      id: 3,
      title: 'HR Management',
      icon: Code,
      description: "Application web de gestion RH (MERN stack) développée pour centraliser la gestion des employés, tâches, demandes internes, documents et départements.",
      technologies: ['React', 'Node.js', 'Express', 'MongoDB'],
      github: 'https://github.com/HAMZAZAWAK17/HR_management',
      period: '2025',
      coverImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center'
    },
  ];

  const ProjectCard = ({ project, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="project-card group cursor-pointer"
    >
      <div className="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-primary-900 dark:to-secondary-900 overflow-hidden">
        <img 
          src={project.coverImage} 
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <project.icon size={48} className="text-white drop-shadow-lg" />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">{project.period}</span>
          <a href={project.github} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-600 dark:hover:text-primary-400" title="Voir sur GitHub">
            <Github size={22} />
          </a>
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{project.title}</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">{project.description}</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {project.technologies.map((tech, i) => (
            <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium">{tech}</span>
          ))}
        </div>
        <a href={project.github} target="_blank" rel="noopener noreferrer" className="btn-secondary mt-2 inline-flex items-center">
          <Github size={18} className="mr-2" /> Voir sur GitHub
        </a>
      </div>
    </motion.div>
  );

  return (
    <section id="projects" className="section-padding bg-white dark:bg-dark-800">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Projets <span className="gradient-text">phares</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Voici quelques projets marquants réalisés récemment. Retrouvez tous mes projets open source sur GitHub !
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <a
            href="https://github.com/HAMZAZAWAK17"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center text-lg"
          >
            <Github size={22} className="mr-2" /> Voir tous les projets sur GitHub
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 